import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Chip,
  Button,
  AppBar,
  Toolbar,
  Tabs,
  Tab,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Rating,
  Fab,
  useScrollTrigger,
  Zoom,
  useTheme,
  useMediaQuery,
  alpha,
  TextField,
  InputAdornment,
  CardActionArea,
  Avatar,
  AvatarGroup,
  Divider,
  Switch,
  FormControlLabel,
  Slide,
  ThemeProvider,
  createTheme
} from '@mui/material';
import {
  Search,
  FilterList,
  Favorite,
  FavoriteBorder,
  Share,
  Close,
  KeyboardArrowUp,
  ShoppingCart,
  Download,
  Launch,
  Code,
  Palette,
  QrCode,
  Image,
  DesignServices,
  ColorLens,
  Bolt,
  TrendingUp,
  NewReleases,
  AccessTime,
  People,
  Star,
  WorkspacePremium,
  Security,
  Speed,
  CameraAlt,
  IntegrationInstructions,
  AutoAwesome
} from '@mui/icons-material';

// Custom theme with your colors
const theme = createTheme({
  palette: {
    primary: {
      main: '#0a1128',
      light: '#1a1f3d',
      dark: '#050a1a',
    },
    secondary: {
      main: '#8b0000',
      light: '#a83232',
      dark: '#620000',
    },
    error: {
      main: '#ff4d4d',
      light: '#ff7979',
      dark: '#cc0000',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
    text: {
      primary: '#0a1128',
      secondary: '#1a1f3d',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h2: {
      fontWeight: 700,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 20px rgba(10, 17, 40, 0.1)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          textTransform: 'none',
        },
        contained: {
          boxShadow: '0 4px 14px rgba(139, 0, 0, 0.3)',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(139, 0, 0, 0.4)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
      },
    },
  },
});

// Enhanced sample data for products and tools
const productsData = [
  {
    id: 1,
    name: 'Logo Color Changer',
    description: 'Transform your brand identity with instant logo color customization. Upload any logo and apply professional color schemes in seconds.',
    image: '/api/placeholder/400/250',
    category: 'Design',
    tags: ['Logo', 'Color', 'Branding', 'Design'],
    rating: 4.8,
    reviews: 1247,
    price: 'Free',
    featured: true,
    popular: true,
    new: false,
    icon: <Palette />,
    link: '/logo-changer',
    usage: '15K+ users',
    features: ['AI Color Matching', 'Batch Processing', 'Brand Guidelines'],
    gradient: 'linear-gradient(135deg, #0a1128 0%, #1a1f3d 100%)'
  },
  {
    id: 2,
    name: 'QR Code Generator',
    description: 'Create stunning, customizable QR codes with advanced analytics and dynamic content management.',
    image: '/api/placeholder/400/250',
    category: 'Marketing',
    tags: ['QR Code', 'Generator', 'Marketing', 'Analytics'],
    rating: 4.6,
    reviews: 892,
    price: 'Free',
    featured: true,
    popular: true,
    new: true,
    icon: <QrCode />,
    link: '/qr-code-generator',
    usage: '12K+ users',
    features: ['Custom Designs', 'Analytics Dashboard', 'Dynamic URLs'],
    gradient: 'linear-gradient(135deg, #8b0000 0%, #ff4d4d 100%)'
  },
  {
    id: 3,
    name: 'Image Optimizer',
    description: 'Advanced image compression and optimization for web and mobile applications without quality loss.',
    image: '/api/placeholder/400/250',
    category: 'Development',
    tags: ['Image', 'Optimization', 'Web', 'Performance'],
    rating: 4.5,
    reviews: 756,
    price: 'Free',
    featured: false,
    popular: false,
    new: false,
    icon: <Image />,
    link: '/image-optimizer',
    usage: '8K+ users',
    features: ['Bulk Processing', 'WebP Conversion', 'Quality Control'],
    gradient: 'linear-gradient(135deg, #1a1f3d 0%, #0a1128 100%)'
  },
  {
    id: 4,
    name: 'UI Component Library',
    description: 'Comprehensive collection of production-ready React components with advanced customization options.',
    image: '/api/placeholder/400/250',
    category: 'Development',
    tags: ['React', 'Components', 'UI', 'Library'],
    rating: 4.9,
    reviews: 2103,
    price: 'Free',
    featured: true,
    popular: true,
    new: false,
    icon: <DesignServices />,
    link: '/ui-components',
    usage: '25K+ users',
    features: ['100+ Components', 'Dark Mode', 'TypeScript'],
    gradient: 'linear-gradient(135deg, #8b0000 0%, #1a1f3d 100%)'
  },
  {
    id: 5,
    name: 'Color Palette Generator',
    description: 'Generate beautiful, accessible color palettes and gradients with advanced contrast checking.',
    image: '/api/placeholder/400/250',
    category: 'Design',
    tags: ['Colors', 'Palette', 'Design', 'Accessibility'],
    rating: 4.7,
    reviews: 967,
    price: 'Free',
    featured: false,
    popular: true,
    new: true,
    icon: <ColorLens />,
    link: '/color-palette',
    usage: '11K+ users',
    features: ['AI Suggestions', 'WCAG Compliance', 'Export Formats'],
    gradient: 'linear-gradient(135deg, #ff4d4d 0%, #8b0000 100%)'
  },
  {
    id: 6,
    name: 'Code Formatter',
    description: 'Intelligent code formatting and beautification with support for 50+ programming languages.',
    image: '/api/placeholder/400/250',
    category: 'Development',
    tags: ['Code', 'Formatter', 'Developer', 'Productivity'],
    rating: 4.4,
    reviews: 634,
    price: 'Free',
    featured: false,
    popular: false,
    new: false,
    icon: <Code />,
    link: '/code-formatter',
    usage: '7K+ users',
    features: ['Multi-language', 'Custom Rules', 'CI/CD Integration'],
    gradient: 'linear-gradient(135deg, #1a1f3d 0%, #0a1128 100%)'
  },
  {
    id: 7,
    name: 'API Integration Hub',
    description: 'Streamline your API integrations with our visual workflow builder and automated testing suite.',
    image: '/api/placeholder/400/250',
    category: 'Development',
    tags: ['API', 'Integration', 'Automation', 'Workflow'],
    rating: 4.8,
    reviews: 543,
    price: 'Free',
    featured: true,
    popular: false,
    new: true,
    icon: <IntegrationInstructions />,
    link: '/api-hub',
    usage: '5K+ users',
    features: ['Visual Builder', 'Auto-testing', '100+ Connectors'],
    gradient: 'linear-gradient(135deg, #0a1128 0%, #8b0000 100%)'
  },
  {
    id: 8,
    name: 'Performance Analyzer',
    description: 'Comprehensive web performance analysis with actionable insights and optimization recommendations.',
    image: '/api/placeholder/400/250',
    category: 'Development',
    tags: ['Performance', 'Analytics', 'Optimization', 'SEO'],
    rating: 4.6,
    reviews: 432,
    price: 'Free',
    featured: false,
    popular: true,
    new: false,
    icon: <Speed />,
    link: '/performance-analyzer',
    usage: '6K+ users',
    features: ['Core Web Vitals', 'Competitor Analysis', 'Automated Reports'],
    gradient: 'linear-gradient(135deg, #8b0000 0%, #ff4d4d 100%)'
  },
  {
  id: 9,
  name: 'Business QR Generator',
  description:
    'Upload your image and embed any link or message as a QR code. Perfect for digital business cards and promotions.',
  image: '/api/placeholder/400/250',
  category: 'Marketing',
  tags: ['QR', 'Business', 'Generator', 'Branding'],
  rating: 4.8,
  reviews: 517,
  price: 'Free',
  featured: true,
  popular: true,
  new: true,
  icon: <QrCode />, // from @mui/icons-material
  link: '/business-qr',
  usage: '9K+ users',
  features: [
    'Upload custom image',
    'Embed links or text as QR',
    'Download & print instantly',
    'Built-in QR scanner'
  ],
  gradient: 'linear-gradient(135deg, #ff8a00 0%, #e52e71 100%)'
},
{
  id: 10,
  name: 'Image-Video Media Scanner',
  description:
    'Link any image to a video and let users scan it to watch automatically — ideal for marketing posters and product demos.',
  image: '/api/placeholder/400/250',
  category: 'AI & Vision',
  tags: ['Scanner', 'Video', 'Image', 'AI'],
  rating: 4.7,
  reviews: 386,
  price: 'Free',
  featured: false,
  popular: true,
  new: true,
  icon: <CameraAlt />, // from @mui/icons-material
  link: '/media-scanner',
  usage: '7K+ users',
  features: [
    'Upload image & video',
    'Auto-generate scannable poster',
    'Camera recognition to play video'
  ],
  gradient: 'linear-gradient(135deg, #004e92 0%, #000428 100%)'
}

];

const categories = ['All', 'Design', 'Development', 'Marketing', 'New', 'Popular'];
const filters = ['All', 'Featured', 'Popular', 'New', 'Free'];

// Framer Motion variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
};

const cardVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  },
  hover: {
    scale: 1.05,
    transition: {
      duration: 0.3,
      ease: "easeInOut"
    }
  },
  tap: {
    scale: 0.98
  }
};

const dialogVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: "easeOut"
    }
  },
  exit: {
    opacity: 0,
    scale: 0.8,
    transition: {
      duration: 0.3,
      ease: "easeIn"
    }
  }
};

const statCardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  },
  hover: {
    y: -5,
    transition: {
      duration: 0.3
    }
  }
};

function ScrollTop({ children }) {
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  const handleClick = (event) => {
    const anchor = (event.target.ownerDocument || document).querySelector(
      '#back-to-top-anchor',
    );
    if (anchor) {
      anchor.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  return (
    <Zoom in={trigger}>
      <Box
        onClick={handleClick}
        role="presentation"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1000,
        }}
      >
        {children}
      </Box>
    </Zoom>
  );
}

const StatCard = ({ icon, value, label, index }) => (
  <motion.div
    variants={statCardVariants}
    initial="hidden"
    animate="visible"
    whileHover="hover"
    custom={index}
  >
    <Box sx={{ textAlign: 'center', p: 2 }}>
      <motion.div
        whileHover={{ scale: 1.1, rotate: 5 }}
        transition={{ type: "spring", stiffness: 300 }}
      >
        <Box sx={{ color: 'primary.main', mb: 1 }}>{icon}</Box>
      </motion.div>
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2 + index * 0.1, type: "spring" }}
      >
        <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          {value}
        </Typography>
      </motion.div>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Box>
  </motion.div>
);

function ProductsToolsPage() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedFilter, setSelectedFilter] = useState('All');
  const [favorites, setFavorites] = useState(new Set());
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  // Fixed tab change handler
  const handleTabChange = (event, newValue) => {
    const category = categories[newValue];
    setSelectedCategory(category);
  };

  const filteredProducts = useMemo(() => {
    return productsData.filter(product => {
      const matchesCategory = selectedCategory === 'All' || 
        product.category === selectedCategory ||
        (selectedCategory === 'New' && product.new) ||
        (selectedCategory === 'Popular' && product.popular);
      
      const matchesFilter = selectedFilter === 'All' ||
        (selectedFilter === 'Featured' && product.featured) ||
        (selectedFilter === 'Popular' && product.popular) ||
        (selectedFilter === 'New' && product.new) ||
        (selectedFilter === 'Free' && product.price === 'Free');
      
      const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      
      return matchesCategory && matchesFilter && matchesSearch;
    });
  }, [selectedCategory, selectedFilter, searchQuery]);

  const toggleFavorite = (productId, event) => {
    if (event) event.stopPropagation();
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(productId)) {
        newFavorites.delete(productId);
      } else {
        newFavorites.add(productId);
      }
      return newFavorites;
    });
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
  };

  const handleCloseDialog = () => {
    setSelectedProduct(null);
  };

  // Fixed navigation handler
  const handleUseTool = (productLink) => {
    navigate(productLink);
    handleCloseDialog();
  };

  // Direct card click handler
  const handleCardClick = (product) => {
    handleProductClick(product);
  };

  const stats = [
    { icon: <People sx={{ fontSize: 40 }} />, value: '50K+', label: 'Active Users' },
    { icon: <AutoAwesome sx={{ fontSize: 40 }} />, value: '25+', label: 'Tools Available' },
    { icon: <WorkspacePremium sx={{ fontSize: 40 }} />, value: '4.8', label: 'Average Rating' },
    { icon: <Download sx={{ fontSize: 40 }} />, value: '1M+', label: 'Downloads' },
  ];

  return (
    <ThemeProvider theme={theme}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ flexGrow: 1, minHeight: '100vh', bgcolor: 'background.default' }}>
          <br/><br/>
          
          <div id="back-to-top-anchor" />

          <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Enhanced Hero Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Box textAlign="center" mb={8}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2, type: "spring" }}
                >
                  <Chip 
                    icon={<NewReleases />} 
                    label="New Tools Added Weekly" 
                    color="primary" 
                    variant="outlined"
                    sx={{ mb: 3 }}
                  />
                </motion.div>
                
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <Typography 
                    variant="h2" 
                    component="h2" 
                    gutterBottom
                    sx={{
                      fontWeight: 'bold',
                      background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      color: 'transparent',
                      fontSize: { xs: '2.5rem', md: '3.5rem' }
                    }}
                  >
                    Build Faster with Our
                    <Box component="span" sx={{ display: 'block' }}>
                      Developer Tools
                    </Box>
                  </Typography>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <Typography 
                    variant="h6" 
                    color="text.secondary" 
                    sx={{ maxWidth: 600, mx: 'auto', mb: 4, fontSize: '1.2rem' }}
                  >
                    Discover powerful, free tools designed to supercharge your workflow and 
                    bring your creative ideas to life faster than ever.
                  </Typography>
                </motion.div>
                
                {/* Stats Section */}
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <Grid container spacing={3} sx={{ mb: 6 }}>
                    {stats.map((stat, index) => (
                      <Grid item xs={6} md={3} key={index}>
                        <StatCard {...stat} index={index} />
                      </Grid>
                    ))}
                  </Grid>
                </motion.div>
              </Box>
            </motion.div>

            {/* Enhanced Search Bar */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <Box sx={{ mb: 6, maxWidth: 600, mx: 'auto' }}>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <TextField
                    fullWidth
                    placeholder="Search tools, features, categories..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery && (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setSearchQuery('')}>
                            <Close />
                          </IconButton>
                        </InputAdornment>
                      ),
                      sx: {
                        borderRadius: 3,
                        bgcolor: 'background.paper',
                        fontSize: '1.1rem',
                        py: 1
                      }
                    }}
                  />
                </motion.div>
              </Box>
            </motion.div>

            {/* Enhanced Category Tabs - Fixed */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
                <Tabs
                  value={categories.indexOf(selectedCategory)}
                  onChange={handleTabChange}
                  variant={isMobile ? "scrollable" : "standard"}
                  scrollButtons="auto"
                  aria-label="product categories"
                  sx={{
                    '& .MuiTab-root': {
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '1rem',
                      minWidth: 100,
                      mx: 1,
                      color: 'text.primary',
                      '&.Mui-selected': {
                        color: 'primary.main',
                      }
                    },
                    '& .MuiTabs-indicator': {
                      backgroundColor: 'secondary.main',
                      height: 3,
                    }
                  }}
                >
                  {categories.map((category, index) => (
                    <Tab 
                      key={category}
                      label={category}
                      icon={
                        category === 'New' ? <NewReleases /> :
                        category === 'Popular' ? <TrendingUp /> : null
                      }
                      iconPosition="start"
                    />
                  ))}
                </Tabs>
              </Box>
            </motion.div>

            {/* Quick Filter Chips */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 4, justifyContent: 'center' }}>
                {filters.map((filter, index) => (
                  <motion.div
                    key={filter}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.9 + index * 0.1, type: "spring" }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Chip
                      label={filter}
                      clickable
                      color={selectedFilter === filter ? 'secondary' : 'default'}
                      variant={selectedFilter === filter ? 'filled' : 'outlined'}
                      onClick={() => setSelectedFilter(filter)}
                      sx={{ 
                        fontWeight: 600,
                        '&.MuiChip-filledSecondary': {
                          bgcolor: 'secondary.main',
                          color: 'white',
                        }
                      }}
                    />
                  </motion.div>
                ))}
              </Box>
            </motion.div>

            {/* Enhanced Products Grid */}
            <AnimatePresence mode="wait">
              <motion.div
                key={`${selectedCategory}-${selectedFilter}-${searchQuery}`}
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
              >
                <Grid container spacing={3}>
                  {filteredProducts.map((product, index) => (
                    <Grid item xs={12} sm={6} lg={4} xl={3} key={product.id}>
                      <motion.div
                        variants={cardVariants}
                        initial="hidden"
                        animate="visible"
                        whileHover="hover"
                        whileTap="tap"
                        custom={index}
                        layout
                      >
                        <Card 
                          sx={{ 
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            border: '1px solid',
                            borderColor: 'divider',
                            cursor: 'pointer',
                            '&:hover': {
                              transform: 'translateY(-8px)',
                              boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
                              borderColor: 'primary.light',
                            },
                          }}
                          onClick={() => handleCardClick(product)}
                        >
                          {/* Card Header with Gradient */}
                          <Box
                            sx={{
                              height: 140,
                              background: product.gradient,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              position: 'relative',
                              overflow: 'hidden'
                            }}
                          >
                            <motion.div
                              whileHover={{ scale: 1.1, rotate: 5 }}
                              transition={{ type: "spring", stiffness: 300 }}
                            >
                              <Box sx={{ fontSize: 48, opacity: 0.9, filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))' }}>
                                {product.icon}
                              </Box>
                            </motion.div>
                            
                            {/* Badges */}
                            <Box sx={{ position: 'absolute', top: 12, left: 12, display: 'flex', gap: 1 }}>
                              {product.featured && (
                                <motion.div
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  transition={{ delay: 0.2, type: "spring" }}
                                >
                                  <Chip
                                    label="Featured"
                                    color="primary"
                                    size="small"
                                    sx={{ 
                                      bgcolor: 'white', 
                                      color: 'primary.main', 
                                      fontWeight: 'bold',
                                      '&:hover': {
                                        bgcolor: 'grey.100',
                                      }
                                    }}
                                  />
                                </motion.div>
                              )}
                              {product.new && (
                                <motion.div
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  transition={{ delay: 0.3, type: "spring" }}
                                >
                                  <Chip
                                    label="New"
                                    color="secondary"
                                    size="small"
                                    sx={{ 
                                      bgcolor: 'white', 
                                      color: 'secondary.main', 
                                      fontWeight: 'bold',
                                      '&:hover': {
                                        bgcolor: 'grey.100',
                                      }
                                    }}
                                  />
                                </motion.div>
                              )}
                            </Box>

                            {/* Favorite Button */}
                            <motion.div
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                            >
                              <IconButton 
                                size="small" 
                                onClick={(e) => toggleFavorite(product.id, e)}
                                sx={{
                                  position: 'absolute',
                                  top: 12,
                                  right: 12,
                                  bgcolor: 'rgba(255,255,255,0.2)',
                                  backdropFilter: 'blur(10px)',
                                  color: 'white',
                                  '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.3)',
                                  }
                                }}
                              >
                                <motion.div
                                  animate={{ scale: favorites.has(product.id) ? 1.2 : 1 }}
                                  transition={{ type: "spring", stiffness: 400 }}
                                >
                                  {favorites.has(product.id) ? <Favorite /> : <FavoriteBorder />}
                                </motion.div>
                              </IconButton>
                            </motion.div>
                          </Box>

                          <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
                            {/* Title and Rating */}
                            <Box sx={{ mb: 2 }}>
                              <motion.div
                                whileHover={{ x: 5 }}
                                transition={{ type: "spring", stiffness: 400 }}
                              >
                                <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600, lineHeight: 1.3, color: 'primary.main' }}>
                                  {product.name}
                                </Typography>
                              </motion.div>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Rating value={product.rating} precision={0.1} size="small" readOnly />
                                <Typography variant="body2" color="text.secondary">
                                  {product.rating} ({product.reviews})
                                </Typography>
                              </Box>
                            </Box>

                            {/* Description */}
                            <Typography 
                              variant="body2" 
                              color="text.secondary" 
                              sx={{ mb: 2, flexGrow: 1, lineHeight: 1.6 }}
                            >
                              {product.description}
                            </Typography>

                            {/* Tags */}
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
                              {product.tags.slice(0, 3).map((tag, tagIndex) => (
                                <motion.div
                                  key={tagIndex}
                                  initial={{ opacity: 0, scale: 0 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  transition={{ delay: 0.4 + tagIndex * 0.1 }}
                                  whileHover={{ scale: 1.1 }}
                                >
                                  <Chip
                                    label={tag}
                                    size="small"
                                    variant="outlined"
                                    sx={{ 
                                      fontSize: '0.7rem', 
                                      height: 24,
                                      borderColor: 'primary.light',
                                      color: 'text.primary'
                                    }}
                                  />
                                </motion.div>
                              ))}
                            </Box>

                            {/* Usage and Price */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                              <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <People sx={{ fontSize: 16 }} />
                                  {product.usage}
                                </Typography>
                              </Box>
                              <motion.div
                                whileHover={{ scale: 1.05 }}
                                transition={{ type: "spring", stiffness: 400 }}
                              >
                                <Typography variant="h6" color="secondary.main" sx={{ fontWeight: 'bold' }}>
                                  {product.price}
                                </Typography>
                              </motion.div>
                            </Box>
                          </CardContent>

                          {/* Action Buttons */}
                          <Box sx={{ p: 2, pt: 0 }}>
                            <motion.div
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              <Button
                                fullWidth
                                variant="contained"
                                startIcon={<Download />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUseTool(product.link);
                                }}
                                sx={{
                                  borderRadius: 2,
                                  py: 1,
                                  fontWeight: 'bold',
                                  background: product.gradient,
                                  color: 'white',
                                  '&:hover': {
                                    transform: 'translateY(-1px)',
                                    boxShadow: `0 8px 20px ${alpha(theme.palette.secondary.main, 0.3)}`,
                                  }
                                }}
                              >
                                Use Tool
                              </Button>
                            </motion.div>
                          </Box>
                        </Card>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </motion.div>
            </AnimatePresence>

            {/* Empty State */}
            <AnimatePresence>
              {filteredProducts.length === 0 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.4 }}
                >
                  <Box textAlign="center" py={8}>
                    <motion.div
                      animate={{ 
                        scale: [1, 1.1, 1],
                        rotate: [0, 5, -5, 0]
                      }}
                      transition={{ 
                        duration: 2,
                        repeat: Infinity,
                        repeatType: "reverse"
                      }}
                    >
                      <AutoAwesome sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    </motion.div>
                    <Typography variant="h5" color="text.secondary" gutterBottom>
                      No tools found
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Try adjusting your search or filter criteria
                    </Typography>
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <Button 
                        variant="outlined" 
                        sx={{ mt: 2 }}
                        onClick={() => {
                          setSearchQuery('');
                          setSelectedCategory('All');
                          setSelectedFilter('All');
                        }}
                      >
                        Clear all filters
                      </Button>
                    </motion.div>
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>
          </Container>

          {/* Enhanced Product Detail Dialog */}
          <AnimatePresence>
            {selectedProduct && (
              <Dialog
                open={!!selectedProduct}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
                TransitionComponent={Slide}
                TransitionProps={{ direction: 'up' }}
              >
                <motion.div
                  variants={dialogVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  {selectedProduct && (
                    <>
                      <DialogTitle sx={{ 
                        p: 0,
                        background: selectedProduct.gradient,
                        color: 'white',
                        position: 'relative'
                      }}>
                        <Box sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <motion.div
                              animate={{ 
                                rotate: [0, 10, -10, 0],
                                scale: [1, 1.1, 1]
                              }}
                              transition={{ 
                                duration: 2,
                                repeat: Infinity,
                                repeatDelay: 3
                              }}
                            >
                              <Box sx={{ fontSize: 48 }}>
                                {selectedProduct.icon}
                              </Box>
                            </motion.div>
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold' }}>
                                {selectedProduct.name}
                              </Typography>
                              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                                {selectedProduct.category} • {selectedProduct.price}
                              </Typography>
                            </Box>
                            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                              <IconButton 
                                onClick={handleCloseDialog}
                                sx={{ color: 'white' }}
                              >
                                <Close />
                              </IconButton>
                            </motion.div>
                          </Box>
                          
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {selectedProduct.tags.map((tag, index) => (
                              <motion.div
                                key={index}
                                initial={{ opacity: 0, scale: 0 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: index * 0.1 }}
                                whileHover={{ scale: 1.1 }}
                              >
                                <Chip
                                  label={tag}
                                  size="small"
                                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                                />
                              </motion.div>
                            ))}
                          </Box>
                        </Box>
                      </DialogTitle>
                      
                      <DialogContent dividers sx={{ p: 3 }}>
                        <Grid container spacing={4}>
                          <Grid item xs={12} md={8}>
                            <motion.div
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.2 }}
                            >
                              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                                About this tool
                              </Typography>
                              <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, color: 'text.primary' }}>
                                {selectedProduct.description}
                              </Typography>
                            </motion.div>
                            
                            <motion.div
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.3 }}
                            >
                              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3, color: 'primary.main' }}>
                                Key Features
                              </Typography>
                              <Grid container spacing={1}>
                                {selectedProduct.features.map((feature, index) => (
                                  <Grid item xs={12} sm={6} key={index}>
                                    <motion.div
                                      initial={{ opacity: 0, x: -10 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      transition={{ delay: 0.4 + index * 0.1 }}
                                      whileHover={{ x: 5 }}
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <motion.div
                                          animate={{ 
                                            scale: [1, 1.2, 1],
                                          }}
                                          transition={{ 
                                            duration: 2,
                                            repeat: Infinity,
                                            repeatDelay: 2
                                          }}
                                        >
                                          <Bolt sx={{ fontSize: 16, color: 'secondary.main' }} />
                                        </motion.div>
                                        <Typography variant="body2" color="text.primary">{feature}</Typography>
                                      </Box>
                                    </motion.div>
                                  </Grid>
                                ))}
                              </Grid>
                            </motion.div>
                          </Grid>
                          
                          <Grid item xs={12} md={4}>
                            <motion.div
                              initial={{ opacity: 0, x: 20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.4 }}
                            >
                              <Box sx={{ bgcolor: 'background.default', borderRadius: 2, p: 3, border: `1px solid ${theme.palette.primary.light}` }}>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                                  Tool Details
                                </Typography>
                                
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                  <Rating value={selectedProduct.rating} precision={0.1} readOnly />
                                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                    {selectedProduct.rating} ({selectedProduct.reviews} reviews)
                                  </Typography>
                                </Box>
                                
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                  <People sx={{ fontSize: 20, color: 'text.secondary' }} />
                                  <Typography variant="body2" color="text.primary">
                                    {selectedProduct.usage}
                                  </Typography>
                                </Box>
                                
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                  <AccessTime sx={{ fontSize: 20, color: 'text.secondary' }} />
                                  <Typography variant="body2" color="text.primary">
                                    Instant access
                                  </Typography>
                                </Box>
                                
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Security sx={{ fontSize: 20, color: 'text.secondary' }} />
                                  <Typography variant="body2" color="text.primary">
                                    Free forever
                                  </Typography>
                                </Box>
                              </Box>
                            </motion.div>
                          </Grid>
                        </Grid>
                      </DialogContent>
                      
                      <DialogActions sx={{ p: 3, gap: 1 }}>
                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                          <Button 
                            startIcon={<Share />}
                            variant="outlined"
                            size="large"
                            sx={{ borderColor: 'primary.main', color: 'primary.main' }}
                          >
                            Share
                          </Button>
                        </motion.div>
                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                          <Button 
                            startIcon={favorites.has(selectedProduct.id) ? <Favorite /> : <FavoriteBorder />}
                            variant="outlined"
                            size="large"
                            onClick={() => toggleFavorite(selectedProduct.id)}
                            color={favorites.has(selectedProduct.id) ? 'error' : 'primary'}
                            sx={{ 
                              borderColor: favorites.has(selectedProduct.id) ? 'error.main' : 'primary.main',
                              color: favorites.has(selectedProduct.id) ? 'error.main' : 'primary.main'
                            }}
                          >
                            {favorites.has(selectedProduct.id) ? 'Favorited' : 'Add to Favorites'}
                          </Button>
                        </motion.div>
                        <motion.div 
                          whileHover={{ scale: 1.05 }} 
                          whileTap={{ scale: 0.95 }}
                          style={{ flexGrow: 1 }}
                        >
                          <Button 
                            variant="contained"
                            startIcon={<Download />}
                            size="large"
                            onClick={() => handleUseTool(selectedProduct.link)}
                            sx={{
                              flexGrow: 1,
                              background: selectedProduct.gradient,
                              py: 1.5,
                              fontWeight: 'bold',
                              color: 'white'
                            }}
                          >
                            Launch Tool
                          </Button>
                        </motion.div>
                      </DialogActions>
                    </>
                  )}
                </motion.div>
              </Dialog>
            )}
          </AnimatePresence>

          {/* Scroll to top button */}
          <ScrollTop>
            <motion.div
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <Fab 
                color="secondary" 
                size="medium" 
                aria-label="scroll back to top"
                sx={{
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                }}
              >
                <KeyboardArrowUp />
              </Fab>
            </motion.div>
          </ScrollTop>
        </Box>
      </motion.div>
    </ThemeProvider>
  );
}

export default ProductsToolsPage;